import React, { useState, useEffect, useRef } from 'react';
import { TYPO, S, SHADCN } from './theme';
import Card from './components/Card';
import Input from './components/Input';
import Button from './components/Button';

const MAX_ATTEMPTS = 5;
const LOCKOUT_SEC  = 30;

export const ROLES = {
  admin:      { label: 'Admin' },
  cafe_admin: { label: 'Admin Café' },
  membre:     { label: 'Membres' },
  comptable:  { label: 'Comptable' },
  bureau_membre: { label: 'Café + Prospection Bureau' },
  cafe_planning: { label: 'Planning du Café' },
};

export default function Login({ onAuth }) {
  const [login,      setLogin]      = useState('');
  const [password,   setPassword]   = useState('');
  const [error,      setError]      = useState(false);
  const [shake,      setShake]      = useState(false);
  const [attempts,   setAttempts]   = useState(0);
  const [lockout,    setLockout]    = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (lockout <= 0) return;
    timerRef.current = setInterval(() => {
      setLockout(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line
  }, [lockout > 0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockout > 0 || submitting) return;
    setSubmitting(true);

    try {
      const res = await fetch('/api/login', {
        method:      'POST',
        credentials: 'same-origin',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify({ login: login.trim(), password }),
      });

      if (res.ok) {
        const data = await res.json();
        setAttempts(0);
        setLogin('');
        setPassword('');
        onAuth(data);
        return;
      }

      if (res.status === 429) {
        const { retryAfter } = await res.json().catch(() => ({ retryAfter: LOCKOUT_SEC }));
        setLockout(retryAfter || LOCKOUT_SEC);
        setError(false);
        setAttempts(0);
        setPassword('');
        return;
      }

      const next = attempts + 1;
      setAttempts(next);
      setError(true);
      setShake(true);
      setPassword('');
      setTimeout(() => setShake(false), 500);
      if (next >= MAX_ATTEMPTS) {
        setAttempts(0);
        setLockout(LOCKOUT_SEC);
        setError(false);
      }
    } catch (_) {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      background: SHADCN.bg,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: SHADCN.font,
      color: SHADCN.fg,
      padding: S[5],
      boxSizing: 'border-box',
    }}>
      <div style={{ marginBottom: S[7], textAlign: 'center' }}>
        <img
          src="/LOGO.png"
          alt="café sonore"
          style={{ height: 32, width: 'auto', display: 'block', margin: '0 auto' }}
        />
      </div>

      <Card
        as="form"
        // eslint-disable-next-line react/no-unknown-property
        onSubmit={handleSubmit}
        className={shake ? 'shake' : ''}
        style={{
          width: 320,
          maxWidth: '100%',
          padding: `${S[6]}px ${S[6]}px ${S[5]}px`,
          marginBottom: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: S[3],
          borderColor: error ? SHADCN.error : SHADCN.border,
          animation: shake ? 'shake 0.4s ease' : undefined,
        }}
      >
        <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg }}>
          Identifiant <span style={{ color: SHADCN.mutedFg, fontWeight: 400 }}>(laisser vide pour admin)</span>
        </div>

        <Input
          type="text"
          value={login}
          onChange={(e) => { setLogin(e.target.value); setError(false); }}
          autoFocus
          disabled={lockout > 0}
          placeholder="prenom"
          autoComplete="username"
          style={{ opacity: lockout > 0 ? 0.4 : 1 }}
        />

        <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg }}>
          Mot de passe
        </div>

        <Input
          type="password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(false); }}
          disabled={lockout > 0}
          placeholder="••••••••"
          autoComplete="current-password"
          error={error}
          style={{ opacity: lockout > 0 ? 0.4 : 1 }}
        />

        {lockout > 0 && (
          <div style={{ ...TYPO.caption, color: SHADCN.error }}>
            Trop de tentatives — réessayez dans {lockout}s
          </div>
        )}
        {error && lockout === 0 && (
          <div style={{ ...TYPO.caption, color: SHADCN.error }}>
            Mot de passe incorrect ({MAX_ATTEMPTS - attempts} essai{MAX_ATTEMPTS - attempts > 1 ? 's' : ''} restant{MAX_ATTEMPTS - attempts > 1 ? 's' : ''})
          </div>
        )}

        <Button
          type="submit"
          fullWidth
          loading={submitting}
          loadingText="Connexion…"
          disabled={lockout > 0}
          style={{ marginTop: S[1] }}
        >
          {lockout > 0 ? `Verrouillé ${lockout}s` : 'Accéder'}
        </Button>
      </Card>

      <style>{`
        input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 1000px ${SHADCN.bg} inset !important;
          -webkit-text-fill-color: ${SHADCN.fg} !important;
        }
      `}</style>
    </div>
  );
}
